import { BCMSRecord, DisabledPrompt, useGlobalController } from "@bcm-dev/bcms-vite";
import { Bill, Biller } from "model";
import React, { useState } from "react";
import { useActivityPageContext } from "../..";

import { ImageAsset, SVGAsset } from "res";

import "./styles.css";

const ActivitySearchContext = React.createContext(null);

/**
 * @function ActivitySearchField
 * @param {STRING} placeholder - The placeholder to be shown in the text field
 * @param {STRING} className - The additional classes that should be added to the search field
 * @returns {React.Component}
 * @exported true
 * @description The main component for the activity search
 * @example
 * <ActivitySearchField placeholder="Testing" />
 */
export default function ActivitySearchField(props) {
    // Initalize a globalContoller, and set the controller for the required model classes
    const globalController = useGlobalController();
    BCMSRecord.globalControllerFor([ Biller, Bill ], globalController);
    // Get the set function of the page context
    const activity = useActivityPageContext();   
    const ref = React.createRef();
    // Set the placeholder and className properties
    const placeholder = props.placeholder ?? "";
    const className = props.className ?? "";

    // Search field is prefilled when navigating from
    // Biller Details when clicking the "View all activity" link.
    const selectedBillerID = props.selectedBillerID ?? "";
    const selectedBiller = Biller.initAll().filter( biller => biller.id === selectedBillerID);
    const defaultSearchValue = selectedBiller.length > 0 ? selectedBiller[0].name : "";

    // Initalize the search field state and controller
    const [ state, setState ] = useState({ "value" : defaultSearchValue, "dropdown" : false });
    const controller = {
        "toggle" : value => setState({ ...state, "dropdown" : value }),
        "set" : state => setState(state),

        "state" : state
    };

    const update = value => controller.set({ "value" : value, "dropdown" : !(value.length === 0) });
    const close = evt => controller.toggle(false);
    const clear = evt => {
        controller.set({ "value" : "", "dropdown" : false });
        activity.setFilter("search", "");
    };
    const onChange = evt => update(ref.current.value);

    return (
        <ActivitySearchContext.Provider value={ controller }>
            {/*<div className="activity-search-field form-row flex-height">
                <div className="search-field-content">
                    <UITextInput placeHolder={placeholder} type="text" value={state.value} onChange={ update }/>
                    
                </div>
                { state.value.length === 0 && 
                    <div>
                        <DisabledPrompt>
                            <SVGAsset className="search-icon clickable" id="MagnifyingGlassIcon" />
                        </DisabledPrompt>
                    </div> }
                { state.value.length > 0 && <SVGAsset id="XIcon" className="icon" onClick={ clear } /> }
                { state.dropdown && <ActivitySearchDropdown /> }
                </div> */}

            <div className={ `activity-search-field form-row flex-height ${ className }` }>
                <div className="search-field-content">
                    <div className="form-row flex-height uitextinput">
                        <div className="floating-label-input-container">
                            <input className="floating-label-input form-row-input" ref={ ref } type="text" placeholder=" " value={state.value} onChange={ onChange } data-point="Activity_Search_Field"></input>
                            <label className="floating-label form-row-label">{placeholder}</label>
                        </div>
                    </div>
                    
                    { state.value.length === 0 &&
                        <div>
                            <DisabledPrompt>
                                <SVGAsset className="search-icon clickable" id="MagnifyingGlassIcon" />
                            </DisabledPrompt>
                        </div> }
                    { state.value.length > 0 && <SVGAsset id="XIcon" className="icon" onClick={ clear } /> }
                    { state.dropdown && <ActivitySearchDropdown /> }
                </div>

                { state.dropdown && <div className="dropdown-overlay" onClick={ close }></div> }
            </div>
        </ActivitySearchContext.Provider>
    );
}

export const useActivitySearchContext = () => React.useContext(ActivitySearchContext);

/**
 * @function ActivitySearchDropdown
 * @returns {React.Component}
 * @exported true
 * @description The dropdown component that shows the billers based on what is entered
 * @example
 * <ActivitySearchDropdown />
 */
export function ActivitySearchDropdown(props) {
    const { state } = useActivitySearchContext();
    
    const billerItems = Biller.initAll()
        .filter(biller => biller.addType !== "verification_add" && biller.addType !== "quick_add", [])
        .filter(biller => biller.name.toLowerCase().includes(state.value.toLowerCase()), [])
        .map(item => <ActivitySearchDropdownItem key={ `Activity_Search_${ item.id }` } biller={ item } />, []);
    const showNoBillers = billerItems.length === 0;

    return (
        <div className="search-for-biller-dropdown flex-container flex-width flex-vertical">
            { showNoBillers && <ActivitySearchNoBillers /> }
            { billerItems }
        </div>
    );
}

/**
 * @function ActivitySearchDropdownItem
 * @param {Biller} biller - The biller to be shown
 * @returns {React.Component}
 * @exported true
 * @description Item component for the activity search dropdown
 * @example
 * <ActivitySearchDropdownItem biller={ biller } />
 */
export function ActivitySearchDropdownItem(props) {
    const { set } = useActivitySearchContext();
    const activity = useActivityPageContext();

    const biller = props.biller;

    const onClick = evt => {
        set({ "value" : biller.name, "dropdown" : false });
        activity.setFilter("search", biller);
    };

    return (
        <div className="row-main-content flex-container flex-horizontal align-center dropdown-item" onClick={ onClick }>
            <div className="logo-container set-width">
                <ImageAsset id={ biller.logo } className="logo-icon" />
            </div>

            <div className="flex-width flex-container flex-vertical">
                <p className="flex-item"><b>{ biller.name }</b></p>
                <p className="flex-item">{ biller.number }</p>
            </div>
        </div>
    );
}

/**
 * @function ActivitySearchNoBillers
 * @returns {React.Component}
 * @exported true
 * @descritpion The dropdown item that is shown when no billers match search
 * @example
 * <ActivitySearchNoBillers />
 */
export function ActivitySearchNoBillers(props) {
    return (
        <div className="row-main-content flex-container flex-horizontal align-center no-match-found dropdown-item">
            <p>Match not found.</p>
        </div>
    );
}